import * as React from 'react';
import { QuestionnaireDesignerProps } from './questionnaire-designer.types';
import { useQuestionnaireDesignerController, QuestionnaireVisiblePage } from './questionnaire-designer.controller';
import { Pages, PagesProps } from './components/pages';
import { Flex } from '@common/components/Flex';
import { Title } from '../title';
import { I18n } from 'react-redux-i18n';
import { Button } from '@common/components/Button';
import { QuestionnairePageType } from '@common/dto/questionnaire/page/type';
import { Page } from './components/page';
import { AffectedFamilyPage } from './components/affected-family-page';
import { FamilyTablePage } from './components/family-table-page';
import { NieceNephewPage } from './components/niece-nephew-page';
import { FamilyBuilderPage } from './components/family-builder-page';
import { CousinPage } from './components/cousin-page';
import { HalfSiblingPage } from './components/half-sibling-page';
import { FinalNotesPage } from './components/final-notes-page';
import { TwinPage } from './components/twin-page';
import { QuestionnairePageListDto } from '@common/dto/questionnaire/page/list-dto';
import { QuestionnairePageCreateLocalDto } from '@modules/fhq-design-tool/dto/page-create-local-dto';
import { QuestionnaireAffectedFamilyPageGetDto } from '@common/dto/questionnaire/page/affected-family/get-dto';
import { QuestionnaireFamilyTablePageGetDto } from '@common/dto/questionnaire/page/family-table/get-dto';
import { QuestionnaireFamilyTablePageUpdateDto } from '@common/dto/questionnaire/page/family-table/update-dto';
import { QuestionnairePageGetDto } from '@common/dto/questionnaire/page/get-dto';
import { QuestionnaireFamilyBuilderPageGetDto } from '@common/dto/questionnaire/page/family-builder/get-dto';
import { QuestionnaireFamilyBuilderPageUpdateDto } from '@common/dto/questionnaire/page/family-builder/update-dto';
import { QuestionnaireCousinPageGetDto } from '@common/dto/questionnaire/page/cousin/get-dto';
import { QuestionnaireCousinPageUpdateDto } from '@common/dto/questionnaire/page/cousin/update-dto';
import { QuestionnaireHalfSiblingPageGetDto } from '@common/dto/questionnaire/page/half-sibling/get-dto';
import { QuestionnaireHalfSiblingPageUpdateDto } from '@common/dto/questionnaire/page/half-sibling/update-dto';
import { QuestionnaireFinalNotesPageGetDto } from '@common/dto/questionnaire/page/final-notes/get-dto';
import { FolderContext } from '@common/dto/folder/context';
import { FolderType } from '@common/dto/folder/type';
import { QuestionnairePageUpdateDto } from '@common/dto/questionnaire/page/update-dto';
import { QuestionnaireAffectedFamilyPageUpdateDto } from '@common/dto/questionnaire/page/affected-family/update-dto';
import { QuestionnaireFinalNotesPageUpdateDto } from '@common/dto/questionnaire/page/final-notes/update-dto';
import { QuestionnaireTwinPageUpdateDto } from '@common/dto/questionnaire/page/twin/update-dto';
import { QuestionnaireTwinPageGetDto } from '@common/dto/questionnaire/page/twin/get-dto';

import './questionnaire-designer.styles.scss';

export const QuestionnaireDesigner: React.FC<QuestionnaireDesignerProps> = (props) => {
  const { 
    isCurrentEditingPageLoadedAlready,
    handleLoadPage,
    sortedPages,
    currentEditingPage,
    resetSelectedPage,
    canChangePageOrder,
    canShowHidePage,
    canDeletePage,
    handlePageChange,
    handlePagesOrderChange,
    handlePageAdd,
    handlePageDelete,
    navigateOnPageEdit,
    handlePageShowStatusChange,
    currentEditingPageErrors
  } = useQuestionnaireDesignerController(props);
  const { showConfirmMessage, getFields, getFolders, scrollParentToTop, getPage, showErrorMessage, errors, getFhq4QuestionnaireField, questionnaire } = props;

  const getClassName = React.useCallback((page: QuestionnaireVisiblePage) => {
    if (errors?.pages && errors.pages[page.id]) {
      return 'FhqDesignToolQuestionnaireDesigner__page--error';
    }
  }, [errors]);

  if (currentEditingPage?.pageType === QuestionnairePageType.normal && getPage) {
    return (
      <Page 
        getFhq4QuestionnaireField={getFhq4QuestionnaireField}
        errors={currentEditingPageErrors}
        showErrorMessage={showErrorMessage}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnairePageGetDto>}
        scrollParentToTop={scrollParentToTop}
        getFields={getFields}
        getFolders={getFolders}
        showConfirmMessage={showConfirmMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnairePageCreateLocalDto | QuestionnairePageUpdateDto
          : undefined}
        data={currentEditingPage as QuestionnairePageListDto}
        onChange={handlePageChange}
        onGoBack={resetSelectedPage}
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.affected && getPage) {
    return (
      <AffectedFamilyPage 
        questionnaire={questionnaire}
        getFhq4QuestionnaireField={getFhq4QuestionnaireField}
        errors={currentEditingPageErrors}
        scrollParentToTop={scrollParentToTop}
        getFields={getFields}
        getFolders={getFolders}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireAffectedFamilyPageGetDto>}
        showConfirmMessage={showConfirmMessage}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireAffectedFamilyPageUpdateDto
          : undefined}
        onChange={handlePageChange as (page: QuestionnaireAffectedFamilyPageUpdateDto) => void}
        onGoBack={resetSelectedPage}
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.familyMembers && getPage) {
    return (
      <FamilyTablePage 
        questionnaire={questionnaire}
        getFhq4QuestionnaireField={getFhq4QuestionnaireField}
        errors={currentEditingPageErrors}
        scrollParentToTop={scrollParentToTop}
        getFields={getFields}
        getFolders={getFolders}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireFamilyTablePageGetDto>}
        showConfirmMessage={showConfirmMessage}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireFamilyTablePageUpdateDto
          : undefined}
        onChange={handlePageChange as (page: QuestionnaireFamilyTablePageUpdateDto) => void}
        onGoBack={resetSelectedPage}
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.finalNotes && getPage) {
    return (
      <FinalNotesPage 
        errors={currentEditingPageErrors}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireFinalNotesPageGetDto>}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireFinalNotesPageUpdateDto
          : undefined}
        onChange={handlePageChange}
        onGoBack={resetSelectedPage}
        getFields={(params) => getFields({ ...params, contextId: FolderContext.pedigree })}
        getFolders={(params) =>
          getFolders({
            ...params,
            folderTypeId: FolderType.pedigree,
            systemFolderConfig: {
              include: false
            },
            setUp: {
              rootFolderName: I18n.t("Pedigree Data Folders"),
              rootFolderImgUrl: "gif/df_pedigree.gif",
            },
          })
        }
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.familyBuilder && getPage) {
    return (
      <FamilyBuilderPage 
        errors={currentEditingPageErrors}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireFamilyBuilderPageGetDto>}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireFamilyBuilderPageUpdateDto
          : undefined}
        onChange={handlePageChange}
        onGoBack={resetSelectedPage}
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.nieceNephew && getPage) {
    return (
      <NieceNephewPage 
        errors={currentEditingPageErrors}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireFamilyBuilderPageGetDto>}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireFamilyBuilderPageUpdateDto
          : undefined}
        onChange={handlePageChange}
        onGoBack={resetSelectedPage}
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.cousin && getPage) {
    return (
      <CousinPage 
        errors={currentEditingPageErrors}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireCousinPageGetDto>}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireCousinPageUpdateDto
          : undefined}
        onChange={handlePageChange}
        onGoBack={resetSelectedPage}
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.halfSibling && getPage) {
    return (
      <HalfSiblingPage 
        errors={currentEditingPageErrors}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireHalfSiblingPageGetDto>}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireHalfSiblingPageUpdateDto
          : undefined}
        onChange={handlePageChange}
        onGoBack={resetSelectedPage}
      />
    );
  }

  if (currentEditingPage?.pageType === QuestionnairePageType.twin && getPage) {
    return (
      <TwinPage 
        errors={currentEditingPageErrors}
        getPage={handleLoadPage as (pageId: QuestionnairePageListDto['id']) => Promise<QuestionnaireTwinPageGetDto>}
        data={currentEditingPage as QuestionnairePageListDto}
        showErrorMessage={showErrorMessage}
        cachedData={isCurrentEditingPageLoadedAlready
          ? currentEditingPage as QuestionnaireTwinPageUpdateDto
          : undefined}
        onChange={handlePageChange}
        onGoBack={resetSelectedPage}
      />
    );
  }
  
  return (
    <div className="FhqDesignToolQuestionnaireDesigner">
      <Flex alignItems="center" className="FhqDesignToolQuestionnaireDesigner__header">
        <Title text={I18n.t('Pages')} margin={0} />
        <Button bsStyle="primary" className="FhqDesignToolQuestionnaireDesigner__add-page-btn" onClick={handlePageAdd}>
          +
        </Button>
      </Flex>
      <Pages 
        getClassName={getClassName as PagesProps<QuestionnaireVisiblePage>['getClassName']}
        data={sortedPages}
        canChangePageOrder={canChangePageOrder}
        canShowHidePage={canShowHidePage}
        canDeletePage={canDeletePage}
        onOrderChange={handlePagesOrderChange}
        showDeleteConfirmMessage={showConfirmMessage}
        onEdit={navigateOnPageEdit}
        onDelete={handlePageDelete}
        onShowStatusChange={handlePageShowStatusChange}
      />
    </div>
  );
};